import React, { useState } from 'react';
import { useGame } from '../context/GameContext';
import { CLASSES, CLASS_LIST, MASTERY_MILESTONES } from '../game/classes';
import { ArrowLeft, Trophy, Sparkles } from 'lucide-react';

const PRESTIGE_BONUSES = [
  { id: 'dmg', name: '+5% DAMAGE', desc: '+5% damage dealt on all future runs of this class.', art: '⚔️' },
  { id: 'hp',  name: '+5% MAX HP', desc: '+5% max HP on all future runs of this class.', art: '❤️' },
  { id: 'gold', name: '+5% GOLD', desc: '+5% gold from all sources on future runs of this class.', art: '💰' },
];

export default function Mastery() {
  const { profile, setScreen, prestigeClass } = useGame();
  const masteries = profile?.masteries || {};
  const [prestigeChoice, setPrestigeChoice] = useState(null);  // {cid, bonusIdx}

  return (
    <div className="min-h-screen w-full px-6 py-6" data-testid="mastery-screen">
      <div className="flex items-center justify-between">
        <button className="brutal-btn text-sm" onClick={() => setScreen('menu')} data-testid="mastery-back">
          <ArrowLeft size={16}/> BACK
        </button>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[color:var(--neon)] flex items-center gap-3">
          <Trophy /> CLASS MASTERY
        </h1>
        <div className="w-24"/>
      </div>
      <div className="mt-4 text-center text-xs uppercase tracking-widest text-[color:var(--text-dim)]">
        Win as a class to level its mastery. LV10+ unlocks PRESTIGE.
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {CLASS_LIST.map(cid => {
          const cls = CLASSES[cid];
          const level = masteries[cid] || 0;
          const prestigeLevel = profile?.prestige_bonuses?.[cid]?.level || 0;
          const canPrestige = level >= 10;
          return (
            <div key={cid} className="brutal-border p-5 bg-[#0a0a0a]" data-testid={`mastery-card-${cid}`}>
              <div className="flex items-center gap-3">
                <div className="text-4xl">{cls.icon}</div>
                <div>
                  <div className="font-display font-black text-xl uppercase" style={{ color: cls.color }}>{cls.name}</div>
                  <div className="text-xs uppercase text-[color:var(--text-dim)]">
                    MASTERY LV. <span data-testid={`mastery-level-${cid}`}>{level}</span>
                    {prestigeLevel > 0 && <span className="ml-2 text-[color:var(--pink)]" data-testid={`prestige-level-${cid}`}>· PRESTIGE {prestigeLevel}</span>}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {MASTERY_MILESTONES.map(m => {
                  const unlocked = level >= m.level;
                  return (
                    <div key={m.level} className={`px-3 py-2 border-2 text-xs ${unlocked ? 'brutal-border-neon text-[color:var(--neon)]' : 'brutal-border text-[color:var(--text-dim)] opacity-50'}`}
                      data-testid={`milestone-${cid}-${m.level}`} data-unlocked={unlocked ? 'true' : 'false'}>
                      <div className="font-mono uppercase tracking-wider">LV {m.level}: {m.name}</div>
                      <div className="text-[10px] normal-case tracking-normal mt-1">{m.desc}</div>
                    </div>
                  );
                })}
              </div>
              {canPrestige && (
                <div className="mt-4 pt-4 border-t-2 border-[color:var(--pink)]">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--pink)] flex items-center gap-1">
                    <Sparkles size={12}/> PRESTIGE READY
                  </div>
                  <button className="brutal-btn brutal-btn-pink w-full mt-2 text-sm" onClick={() => setPrestigeChoice({ cid })}
                    data-testid={`prestige-btn-${cid}`}>
                    PRESTIGE (RESET TO LV0)
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Prestige confirmation modal */}
      {prestigeChoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85" data-testid="prestige-modal">
          <div className="brutal-border-pink bg-[#0a0a0a] p-6 max-w-lg w-full mx-4">
            <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--pink)]">PRESTIGE {CLASSES[prestigeChoice.cid].name}</div>
            <h3 className="font-display font-black text-2xl uppercase mt-2 text-[color:var(--pink)]">CHOOSE YOUR ETERNAL BLESSING</h3>
            <div className="text-xs text-[color:var(--text-dim)] mt-2">
              Your mastery resets to LV0. Pick ONE permanent bonus applied to ALL future runs of this class. Stackable — do it every 10 levels.
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              {PRESTIGE_BONUSES.map(pb => (
                <button
                  key={pb.id}
                  className="brutal-btn text-left flex items-center gap-3"
                  onClick={async () => {
                    await prestigeClass(prestigeChoice.cid, pb.id);
                    setPrestigeChoice(null);
                  }}
                  data-testid={`prestige-choice-${pb.id}`}
                >
                  <div className="text-3xl">{pb.art}</div>
                  <div>
                    <div className="font-display font-black text-[color:var(--neon)]">{pb.name}</div>
                    <div className="text-xs text-[color:var(--text)]">{pb.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            <button className="brutal-btn w-full mt-4" onClick={() => setPrestigeChoice(null)} data-testid="prestige-cancel">
              CANCEL
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
